import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  BudgetDetail,
  BudgetListItem,
  BudgetStatus,
  CreateBudgetPayload,
  PaginatedResponse,
  UpdateBudgetPayload,
} from "../types";

const BUDGETS_KEY = ["budgets"] as const;

export type BudgetListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  type?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  fiscalYear?: number;
};

export function useBudgetList(
  params: BudgetListParams = {},
): UseQueryResult<PaginatedResponse<BudgetListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...BUDGETS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<BudgetListItem>>("/budgets", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          type: params.type,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
          fiscalYear: params.fiscalYear,
        },
      }),
  });
}

export function useBudget(id: string): UseQueryResult<BudgetDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...BUDGETS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ budget: BudgetDetail }>(`/budgets/${id}`, {
        token: accessToken,
      });
      return res.budget;
    },
  });
}

export function useCreateBudget() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBudgetPayload) =>
      apiFetch<{ budget: BudgetDetail }>("/budgets", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...BUDGETS_KEY] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useUpdateBudget(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateBudgetPayload) =>
      apiFetch<{ budget: BudgetDetail }>(`/budgets/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...BUDGETS_KEY] });
    },
  });
}

export function useUpdateBudgetStatus() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: BudgetStatus }) =>
      apiFetch<{ budget: BudgetDetail }>(`/budgets/${id}/status`, {
        method: "PUT",
        body: { status },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...BUDGETS_KEY] });
    },
  });
}

export function useDeleteBudget() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/budgets/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...BUDGETS_KEY] });
    },
  });
}

export function useBudgetExpenses(budgetId: string) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...BUDGETS_KEY, budgetId, "expenses", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!budgetId,
    queryFn: () =>
      apiFetch<PaginatedResponse<unknown>>(`/budgets/${budgetId}/expenses`, {
        token: accessToken,
      }),
  });
}
